import React, { Suspense, useEffect } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import Loader from "src/components/common/Loader";
import { BackgroundJobsProvider } from "src/contexts/BackgroundJobsContext";
import { WebSocketProvider } from "src/contexts/WebSocketContext";
import { useRecentlyVisitedCluster } from "src/experiences/cluster-experience/hooks/useRecentlyVisitedCluster";
import useUser from "src/hooks/useUser";
import Routes from "src/utils/Routes";

const ClusterExperience = React.lazy(
  () => import("src/experiences/cluster-experience/ClusterExperience")
);
const EntityExperience = React.lazy(
  () => import("src/experiences/entity-experience/EntityExperience")
);
const EntityLandingPage = React.lazy(
  () => import("src/experiences/entity-experience/EntityLandingPage")
);
const DocumentsDownload = React.lazy(
  () => import("src/experiences/EntityExperience/Documents/DocumentsDownload")
);

const suspense = (element: React.ReactNode) => (
  <Suspense fallback={<Loader />}>{element}</Suspense>
);

const UserLandingPage = () => {
  const { cluster, entity, memberships } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { getRecentlyVisitedClusterId } = useRecentlyVisitedCluster();

  const loggedInRoutes = [
    {
      path: "/entities/home/*",
      element: suspense(<EntityLandingPage rootUrl="/entities" />),
      enabled: memberships.length > 1,
    },
    {
      path: "/clusters/:clusterId/*",
      element: suspense(<ClusterExperience />),
    },
    {
      path: "/entities/:entityId/*",
      element: suspense(<EntityExperience />),
    },
    { path: "/documents/*", element: suspense(<DocumentsDownload />) },
  ];

  useEffect(() => {
    if (location.pathname.startsWith("/documents")) {
      return;
    }

    // If we're on a cluster route, let that take precedence
    if (location.pathname.startsWith("/clusters/")) {
      return;
    }

    // For cluster users, try to redirect to their last visited cluster
    if (cluster) {
      const lastVisitedClusterId = getRecentlyVisitedClusterId();

      // If there is a last visited cluster and it is in their memberships - go there
      if (
        lastVisitedClusterId &&
        memberships.some((m) => m.cluster?.id === lastVisitedClusterId)
      ) {
        navigate(`/clusters/${lastVisitedClusterId}`);
        return;
      }

      // Otherwise, use their current cluster
      navigate(`/clusters/${cluster.id}`);
      return;
    }

    // this simple conditional switch will die when we roll the new borrower experience which consolidates landing
    // multi membership and the current entity experience
    if (entity && !location.pathname.startsWith("/entities/")) {
      if (memberships.length > 1) {
        navigate("entities/home/");
      } else {
        navigate(`/entities/${entity.id}`);
      }
    }
  }, []);
  return (
    <WebSocketProvider>
      <BackgroundJobsProvider>
        <Routes routes={loggedInRoutes} rootUrl={""} />
      </BackgroundJobsProvider>
    </WebSocketProvider>
  );
};

export default UserLandingPage;
